/**
 * HTML Viewer Module — Sandboxed iframe renderer for agent-generated content.
 *
 * Config shapes:
 *   { html: string }                — inline HTML rendered via srcdoc
 *   { url: string }                 — external URL rendered via src (future: Python apps, etc.)
 *   { html: string, title: string } — with optional title override
 *
 * Security:
 *   - sandbox="allow-scripts" only (no allow-same-origin — prevents XSS/cookie access)
 *   - Content is fully isolated from the host page
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Module } from '@/types/modules';
import { useSession } from '@/features/auth/hooks/useSession';
import { getActiveWorkspaceId } from '@/lib/api/workspace-context';
import { queryKeys } from '@/lib/query/keys';
import { publishReport, unpublishReport } from '@/lib/api/endpoints/workspaces';
import { getLiveHtml } from '@/lib/html-live-state';
import { toCanonicalPageId } from '@/lib/page-id';
import { getInspectorScript } from '@/lib/inspector-script';
import PagePreviewModal from '@/modules/pages/PagePreviewModal';
import { resolveUrl } from '../utils/page-renderer-helpers';
import '../report/report.css';

interface HtmlViewerModuleProps {
  module: Module;
  conversationId?: string;
}

interface HtmlViewerConfig {
  html?: string;
  url?: string;
  title?: string;
  publish?: {
    slug: string;
    url: string;
    visibility: string;
    publishedAt: string;
  };
}

export default function HtmlViewerModule({ module }: HtmlViewerModuleProps) {
  const config = (module.config as HtmlViewerConfig) || {};

  // Live-stream CustomEvents (xavior:html-config-update / -section-update) carry
  // a `moduleId` that applyHtmlDirective canonicalises via toCanonicalPageId —
  // i.e. `pg_<rawModuleId>` for a fresh module-sourced build with no page open.
  // This module is keyed on the RAW module.id, so we must accept BOTH forms or
  // every live chunk after the mount-time seed is dropped and the page freezes
  // mid-build. (#pages-streaming-regression)
  const canonicalModuleId = useMemo(
    () => toCanonicalPageId(module.id) ?? module.id,
    [module.id],
  );
  const matchesLiveId = useCallback(
    (incoming: unknown): boolean =>
      typeof incoming === 'string' &&
      (incoming === module.id || incoming === canonicalModuleId),
    [module.id, canonicalModuleId],
  );

  // Live HTML state for progressive streaming.
  // Seeded from the shared live-state map on mount to avoid missing chunks
  // that were dispatched BEFORE this component mounted (race when
  // PagesContainerModule opens the module after the first CustomEvent fires).
  const [liveHtml, setLiveHtml] = useState<string | null>(() => {
    const entry = getLiveHtml(module.id);
    return entry?.html || null;
  });
  // Tracks whether the build stream has signaled completion. Lets us hide the
  // "Building page..." indicator immediately on html.complete without waiting
  // for the modules query to refetch.
  const [streamFinished, setStreamFinished] = useState(() => {
    const entry = getLiveHtml(module.id);
    return entry?.final ?? false;
  });

  // rAF-coalesced live-html setter: upstream dispatches
  // `xavior:html-config-update` roughly every ~300ms with the full cumulative
  // HTML string. Calling `setLiveHtml` on every event re-renders the tree and
  // reassigns the iframe's `srcDoc` prop — forcing a full document reparse +
  // repaint per chunk. Coalescing through rAF collapses bursts into at most
  // one state commit per paint while always retaining the LATEST cumulative
  // string (nothing is dropped).
  const pendingHtmlRef = useRef<string | null>(null);
  const htmlRafRef = useRef<number | null>(null);
  const scheduleLiveHtml = useCallback((html: string) => {
    pendingHtmlRef.current = html;
    if (htmlRafRef.current != null) return;
    htmlRafRef.current = requestAnimationFrame(() => {
      htmlRafRef.current = null;
      if (pendingHtmlRef.current != null) {
        setLiveHtml(pendingHtmlRef.current);
        pendingHtmlRef.current = null;
      }
    });
  }, []);

  useEffect(() => () => {
    if (htmlRafRef.current != null) {
      cancelAnimationFrame(htmlRafRef.current);
      htmlRafRef.current = null;
    }
  }, []);

  // Listen for streaming HTML updates from useChat
  useEffect(() => {
    function onHtmlUpdate(e: Event) {
      const detail = (e as CustomEvent).detail as { moduleId: string; html: string; final?: boolean };
      if (!matchesLiveId(detail.moduleId)) return;
      if (detail.html) scheduleLiveHtml(detail.html);
      if (detail.final) setStreamFinished(true);
    }
    window.addEventListener('xavior:html-config-update', onHtmlUpdate as EventListener);
    return () => window.removeEventListener('xavior:html-config-update', onHtmlUpdate as EventListener);
  }, [matchesLiveId, scheduleLiveHtml]);

  // Reset live state when module changes (e.g., reopening from Pages list).
  // Also re-seed from the shared map in case we're navigating to a build in
  // progress (would be caught by listener too, but this is instant).
  useEffect(() => {
    const entry = getLiveHtml(module.id);
    setLiveHtml(entry?.html || null);
    setStreamFinished(entry?.final ?? false);
  }, [module.id]);

  // Clear live state when module config updates from API (final persist)
  useEffect(() => {
    if (config.html && liveHtml) {
      setLiveHtml(null); // Config caught up, stop using live state
    }
  }, [config.html]);

  const { token, selectedContainer } = useSession();
  const queryClient = useQueryClient();
  const [publishing, setPublishing] = useState(false);
  const [publicUrl, setPublicUrl] = useState(config.publish?.url || '');
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Sync publish URL from config. Also re-seeds when the user switches to a
  // different module — `module.id` in deps prevents the previous module's
  // publicUrl from sticking around when the underlying record changes.
  useEffect(() => {
    setPublicUrl(config.publish?.url || '');
  }, [config.publish?.url, module.id]);

  const resolveWorkspaceId = useCallback(() => {
    return selectedContainer?.id ?? selectedContainer?.ownerId ?? getActiveWorkspaceId();
  }, [selectedContainer]);

  // NOTE: We intentionally DO NOT auto-size the iframe based on its content's
  // scrollHeight. Doing so creates a feedback loop with viewport units (`vh`):
  //   1. Iframe height = scrollHeight
  //   2. Content uses `min-height: 100vh` for the hero
  //   3. `100vh` resolves to the iframe's CSS height
  //   4. Hero grows → scrollHeight grows → iframe grows → vh grows → hero grows...
  //   5. Runaway loop until the cap, hero ends up massively stretched on desktop
  //
  // Mobile share works because vh resolves to the phone's actual screen, not
  // a self-resizing iframe. To match that behavior, we keep the iframe at a
  // fixed `height: 100%` of its container and let it scroll internally —
  // exactly like an actual mobile browser viewport.

  const handlePublish = useCallback(async () => {
    if (publishing) return;
    setPublishing(true);
    setError(null);
    try {
      const workspaceId = resolveWorkspaceId();
      const result = await publishReport(token ?? null, workspaceId, module.id);
      setPublicUrl(result.url);
    } catch (err) {
      console.error('[HtmlViewer] Publish failed:', err);
      setError('Failed to publish');
      setTimeout(() => setError(null), 3000);
    } finally {
      setPublishing(false);
    }
  }, [publishing, token, module.id, resolveWorkspaceId]);

  const resolvedUrl = publicUrl ? resolveUrl(publicUrl) : null;

  // Two distinct actions, two distinct buttons — same split as PageRenderer.
  // The previous `handleCopy` toggled to `navigator.share()` whenever it was
  // available, which meant on Chrome/Safari (including desktop) clicking the
  // copy-link icon opened the OS share sheet instead of copying — and the
  // separate Share button below also routed to handleCopy, so the two
  // buttons did the exact same thing. Copy now unconditionally writes to
  // the clipboard; the Share button uses handleNativeShare.
  const handleCopy = useCallback(async () => {
    if (!resolvedUrl) return;
    try {
      await navigator.clipboard.writeText(resolvedUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.warn('[HtmlViewer] clipboard.writeText failed', err);
    }
  }, [resolvedUrl]);

  const handleNativeShare = useCallback(async () => {
    if (!resolvedUrl) return;
    if (typeof navigator === 'undefined' || typeof navigator.share !== 'function') return;
    try {
      await navigator.share({ url: resolvedUrl });
    } catch {
      // User cancelled share — not an error
    }
  }, [resolvedUrl]);

  const handleDownload = useCallback(() => {
    if (!config.html) return;
    const blob = new Blob([config.html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${config.title || module.label || 'page'}.html`;
    a.click();
    URL.revokeObjectURL(url);
  }, [config.html, config.title, module.label]);

  const handleUnpublish = useCallback(async () => {
    setError(null);
    try {
      const workspaceId = resolveWorkspaceId();
      await unpublishReport(token ?? null, workspaceId, module.id);
      setPublicUrl('');
      queryClient.invalidateQueries({ queryKey: queryKeys.modules.list(workspaceId) });
    } catch (err) {
      console.error('[HtmlViewer] Unpublish failed:', err);
      setError('Failed to unpublish');
      setTimeout(() => setError(null), 3000);
    }
  }, [token, module.id, queryClient, resolveWorkspaceId]);

  // Prefer live streaming HTML over static config
  const effectiveHtml = liveHtml || config.html;

  // Stable ref to the iframe element so parent re-renders don't swap the
  // DOM node (and therefore don't reset internal scroll position during
  // streaming). We never assign a changing `key` prop to this iframe.
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Per-render nonce embedded into the inspector script and stamped into
  // every patch postMessage. The iframe-side listener rejects messages
  // whose `n` field does not match — mirrors PageRenderer's nonce protocol.
  const patchNonceRef = useRef<string>(
    typeof crypto !== 'undefined' && typeof (crypto as any).randomUUID === 'function'
      ? (crypto as any).randomUUID()
      : 'n-' + Math.random().toString(36).slice(2) + Date.now().toString(36),
  );

  // True only after the iframe has fired its `load` event. Patch messages
  // posted before this point would race the iframe's initial parse.
  const iframeReadyRef = useRef<boolean>(false);

  // Section frames that arrived while the iframe was mid-parse. Dropping
  // them (the old behavior) left every streamed section missing from this
  // surface until the next cumulative reload — and during live builds the
  // iframe is mid-parse most of the time. Queued frames drain on load;
  // re-applying a section that the srcDoc already contains is an in-place
  // replace, so the drain is harmless when the cumulative html caught up.
  const pendingFramesRef = useRef<Array<{ sectionId: string; html: string }>>([]);

  // Reset the iframe-ready flag whenever effectiveHtml changes so that a new
  // srcDoc assignment (full reload path) isn't treated as ready before onLoad.
  useEffect(() => {
    iframeReadyRef.current = false;
  }, [effectiveHtml]);

  const postSectionFrame = useCallback((frame: { sectionId: string; html: string }) => {
    const win = iframeRef.current?.contentWindow;
    if (!win) return;
    win.postMessage(
      {
        type: 'xavior-patch-section',
        sectionId: frame.sectionId,
        outerHTML: frame.html,
        n: patchNonceRef.current,
      },
      '*',
    );
  }, []);

  // onLoad handler: mark iframe ready, send the nonce init message so the
  // inspector script starts accepting patch messages from this parent, then
  // drain any frames that were queued during the parse window.
  const handleIframeLoad = useCallback(() => {
    iframeReadyRef.current = true;
    const win = iframeRef.current?.contentWindow;
    if (win) {
      win.postMessage({ type: 'xavior-init-nonce', nonce: patchNonceRef.current }, '*');
    }
    const frames = pendingFramesRef.current;
    pendingFramesRef.current = [];
    for (const frame of frames) postSectionFrame(frame);
  }, [postSectionFrame]);

  // Section-update listener: forward xavior:html-section-update events
  // to the iframe via postMessage so the inspector script can apply
  // surgical DOM patches instead of forcing a full srcDoc reassign.
  // Scoped by module.id so patches from page A don't land in page B.
  useEffect(() => {
    const onSectionUpdate = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || !matchesLiveId(detail.moduleId)) return;
      if (!iframeReadyRef.current) {
        // Iframe mid-parse — queue for the onLoad drain (mirrors
        // PageRenderer.pendingSectionFramesRef; dropping lost the build).
        pendingFramesRef.current.push({ sectionId: detail.sectionId, html: detail.html });
        return;
      }
      postSectionFrame({ sectionId: detail.sectionId, html: detail.html });
    };
    window.addEventListener('xavior:html-section-update', onSectionUpdate);
    return () => window.removeEventListener('xavior:html-section-update', onSectionUpdate);
  }, [matchesLiveId, postSectionFrame]);

  // Patch-failure handling — formerly surfaced via a red banner overlay in
  // the iframe wrap. The banner was confusing more often than it was useful
  // (transient races, race-on-disconnect, etc. all fired it), so we removed
  // the visible UI but kept the listener for telemetry / future hooks. The
  // patch-failure path itself triggers a full srcDoc reload elsewhere, so
  // the user never sees stale state — they just don't see a scary banner.
  useEffect(() => {
    const onFail = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || detail.moduleId !== module.id) return;
      console.warn('[HtmlViewer] patch-failed (banner suppressed):', detail);
    };
    window.addEventListener('xavior:patch-failed', onFail);
    return () => window.removeEventListener('xavior:patch-failed', onFail);
  }, [module.id]);

  // Forward iframe-posted xavior-patch-failed postMessages into the unified
  // CustomEvent channel AND self-heal: re-render the iframe from the latest
  // cumulative live html, which contains every section streamed so far. A
  // mid-stream section-not-found (frame raced the shell parse, stale doc,
  // etc.) therefore recovers on its own — the forwarded event carries
  // `recovered: true` so the global toast (ToastContext) stays quiet for
  // these transients and only surfaces failures nothing recovered from.
  // Requires nonce match to prevent a rogue sibling iframe from triggering
  // spurious reloads.
  const lastRecoverAtRef = useRef(0);
  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== 'null') return; // sandboxed iframes post from origin 'null'
      if (e.source !== iframeRef.current?.contentWindow) return; // only THIS iframe
      if (!e.data || e.data.type !== 'xavior-patch-failed') return;
      if (e.data.n !== patchNonceRef.current) return; // nonce guard
      // Recovery: pull the freshest cumulative html (raw module key first —
      // the build flow's live-state key — then the canonical mirror key) and
      // commit it through React so the srcDoc safety-wrapper re-applies.
      // Throttled so a pathological patch loop can't thrash full reloads.
      let recovered = false;
      const now = Date.now();
      if (now - lastRecoverAtRef.current > 2000) {
        const latest = getLiveHtml(module.id)?.html || getLiveHtml(canonicalModuleId)?.html;
        if (latest) {
          lastRecoverAtRef.current = now;
          iframeReadyRef.current = false;
          pendingFramesRef.current = [];
          // Same-string commits don't re-render; nudge unconditionally.
          setLiveHtml(latest + `<!--xavior-recover-${now}-->`);
          recovered = true;
        }
      }
      window.dispatchEvent(new CustomEvent('xavior:patch-failed', {
        detail: {
          moduleId: module.id,
          sectionId: e.data.sectionId,
          reason: e.data.reason ?? 'dom-apply-failed',
          recovered,
        },
      }));
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [module.id, canonicalModuleId]);

  // Inject a CSS reset into the iframe srcdoc that:
  //   1. Caps any element using `100vh` for hero height (the agent sometimes
  //      still emits this despite skill instructions, AND old pages have it
  //      baked in). Without this, on a wide desktop iframe the hero stretches
  //      to the entire iframe height which can be huge.
  //   2. Forces images to behave responsively even if the agent forgot
  //      `max-width: 100%` (defense in depth — also covered by skill).
  //   3. Sets html/body to fill the iframe so the user's content can
  //      legitimately use `height: 100%` patterns.
  //
  // Injected at the top of <head> so user CSS can override if needed (their
  // rules come later in the cascade).
  // Memoized: the safety-styles literal is stable for the lifetime of the
  // component and was previously rebuilt on every streaming chunk (~300ms).
  // Hoisting into `useMemo` avoids re-allocating the string during bursts.
  const safetyStyles = useMemo(() => `<style>
html, body { margin: 0; padding: 0; min-height: 100%; }
img, video { max-width: 100%; height: auto; display: block; }
/* Cap any hero / banner / first section so viewport-height units like 100vh,
   100dvh, 100svh, 100lvh inside the iframe can't stretch the first paint to
   the iframe's full content height (which would create a feedback loop with
   any auto-sizing). Covers both class-named heroes and structural first-of-type
   patterns the agent commonly emits. */
.hero, .Hero, .HERO,
header.hero, section.hero,
[class*="hero" i], [class*="banner" i], [class*="jumbotron" i],
[class*="masthead" i], [class*="splash" i], [class*="cover" i],
[class*="intro" i],
header:first-of-type, section:first-of-type,
body > div:first-child > section:first-of-type,
body > div:first-child > header:first-of-type {
  max-height: clamp(420px, 80vh, 760px);
}
/* Also clamp the very first direct child of body when it's a div masquerading
   as a hero (no semantic header/section). */
body > div:first-child[class*="hero" i],
body > div:first-child[class*="banner" i],
body > div:first-child[class*="jumbotron" i] {
  max-height: clamp(420px, 80vh, 760px);
}
</style>`, []);
  // Build the injected head prefix: safety styles + inspector script with nonce.
  // The inspector script is regenerated here only when safetyStyles changes
  // (i.e., never after mount). The nonce is read from a ref so this memo
  // doesn't need to re-run when the nonce rotates — patches carry the nonce
  // at send time via patchNonceRef.current.
  const injectedHead = useMemo(
    () => safetyStyles + getInspectorScript(patchNonceRef.current),
    [safetyStyles],
  );

  const htmlWithSafety = useMemo(() => {
    if (!effectiveHtml) return undefined;
    if (effectiveHtml.includes('<head>')) return effectiveHtml.replace('<head>', `<head>${injectedHead}`);
    if (effectiveHtml.includes('</head>')) return effectiveHtml.replace('</head>', `${injectedHead}</head>`);
    return `${injectedHead}${effectiveHtml}`;
  }, [effectiveHtml, injectedHead]);

  if (!effectiveHtml && !config.url) {
    // If the module was created very recently (within the build window), the
    // agent is probably about to stream content into it. Show a building
    // skeleton instead of the empty-state copy so the user doesn't see
    // "No content to display" for a multi-second head-buffer window.
    const moduleAgeMs = module.createdAt
      ? Date.now() - new Date(module.createdAt).getTime()
      : Infinity;
    const isLikelyBuilding = moduleAgeMs < 30_000;

    if (isLikelyBuilding) {
      return (
        <div
          className="module"
          style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', minHeight: 0 }}
        >
          <div className="xavior-build-loader">
            <div className="xavior-build-loader__logo-wrap">
              <div className="xavior-build-loader__glow" />
              <img src="/favicon.svg" alt="Xavior" className="xavior-build-loader__logo" />
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="module"
        style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%', minHeight: 0 }}
      >
        <div
          className="module-content"
          style={{ padding: 16, color: 'var(--color-text-secondary)', flex: 1, minHeight: 0 }}
        >
          No content to display. The agent can create visualizations here.
        </div>
      </div>
    );
  }

  return (
    <div
      className="module"
      style={{
        display: 'flex',
        flexDirection: 'column',
        // Both `flex: 1` (for when we're inside a flex column parent) AND
        // `height: 100%` (for when we're inside a block parent) — belt and
        // suspenders so the wrapper fills its container in every context.
        flex: 1,
        height: '100%',
        minHeight: 0,
      }}
    >
      {/* Share / Download / Publish bar — mirrors PageRenderer's webpage
          action bar so the in-chat post-build view looks identical to the
          view a user gets when they click the same page from the Pages
          list later (issue: chat-vs-pages action-bar inconsistency).
          Gates on `effectiveHtml` (liveHtml || config.html) so the bar is
          visible during streaming builds, not just after persistence.
          Edit / Inspect mode lives only on PageRenderer (object-sourced
          pages) — html_viewer modules don't have an inspector, so we skip
          the Edit button here. Order matches PageRenderer otherwise:
            Pre-publish:  Download → Preview → Publish
            Post-publish: Download → Copy → Share → Preview → Unpublish

          Status / action-controls visibility flip on `streamFinished`:
          while the build is streaming, the pill reads "Building..." and
          no action buttons render (you can't share/publish/copy a
          half-built page). The moment `streamFinished` flips to true
          (final html.complete dispatch lands), the pill swaps to
          "Ready" and the full action bar fades in — IN PLACE, with the
          iframe untouched. No reload, no flicker. */}
      {effectiveHtml && (
        <div className={`report-status-bar complete`}>
          <span className="status-dot" />
          {/* Show "Ready" + action bar if EITHER the stream just finished
              OR the module has persisted html. The latter handles the
              re-mount case: when the user navigates away from a streaming
              page and back, the useEffect([module.id]) re-seeds
              streamFinished from the live-state map which may briefly
              read false — but `config.html` is already populated from
              the prior persist, so the page is fully usable. Without
              this OR, the action bar would flicker out on every remount
              of an already-built page. */}
          <span>{error || (streamFinished || !!config.html ? 'Ready' : 'Building...')}</span>
          {(streamFinished || !!config.html) && (
          <div className="report-share-controls">
            {/* Download — always available, even pre-publish. */}
            <button
              type="button"
              className="report-share-icon-btn"
              onClick={handleDownload}
              aria-label="Download HTML"
              title="Download"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5" />
                <path d="M7.5 12L12 16.5m0 0L16.5 12M12 16.5V3" />
              </svg>
            </button>

            {/* Copy link — only after publish (no URL to copy beforehand).
                Icon swaps to a checkmark for ~2s after a successful copy. */}
            {publicUrl && (
              <button
                type="button"
                className="report-share-icon-btn"
                onClick={handleCopy}
                title={copied ? 'Copied!' : 'Copy link'}
                aria-label={copied ? 'Copied!' : 'Copy link'}
              >
                {copied ? (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                ) : (
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                )}
              </button>
            )}

            {/* Share — post-publish only, opens the native share sheet
                when the browser supports navigator.share. */}
            {publicUrl && typeof navigator !== 'undefined' && typeof navigator.share === 'function' && (
              <button className="report-share-link-btn" onClick={handleNativeShare} title="Open share sheet">
                Share
              </button>
            )}

            {/* Preview — fullscreen modal preview of the rendered page. */}
            <button
              className="report-share-link-btn"
              onClick={() => setPreviewOpen(true)}
              title="Fullscreen preview"
            >
              Preview
            </button>

            {/* Primary action — always rightmost.
                  Pre-publish:  Publish (with inline spinner that swaps in
                                while a publish is in flight).
                  Post-publish: Unpublish (inverted-Publish styling). */}
            {publicUrl ? (
              <button className="report-unpublish-btn" onClick={handleUnpublish} title="Unpublish">
                Unpublish
              </button>
            ) : (
              <button
                className="report-publish-btn"
                onClick={handlePublish}
                disabled={publishing}
                aria-label={publishing ? 'Publishing…' : 'Publish'}
              >
                {publishing ? (
                  <svg
                    className="report-publish-spinner"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <circle cx="12" cy="12" r="10" opacity="0.25" />
                    <path d="M12 2a10 10 0 0 1 10 10" />
                  </svg>
                ) : (
                  'Publish'
                )}
              </button>
            )}
          </div>
          )}
        </div>
      )}
      {previewOpen && effectiveHtml && (
        <PagePreviewModal
          html={effectiveHtml}
          title={module.label}
          onClose={() => setPreviewOpen(false)}
        />
      )}
      {/*
        Deliberately NOT using className="module-content" here — the global
        CSS rule `.chat-module-pane-content .module-content { overflow-y:
        auto !important; height: 100% !important }` in ChatBox.css competes
        with absolute-positioned iframe fill and collapses the box. Using a
        plain class name avoids the !important overrides entirely.
      */}
      <div
        className="html-viewer-iframe-wrap"
        style={{
          padding: 0,
          flex: 1,
          minHeight: 0,
          position: 'relative',
          overflow: 'hidden',
          width: '100%',
        }}
      >
        <iframe
          ref={iframeRef}
          title={config.title || module.label || 'HTML Viewer'}
          sandbox="allow-scripts"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            borderRadius: 'var(--radius-md, 8px)',
            background: 'var(--color-surface)',
            display: 'block',
          }}
          onLoad={handleIframeLoad}
          {...(config.url
            ? { src: config.url }
            : { srcDoc: htmlWithSafety || effectiveHtml })}
        />
      </div>
    </div>
  );
}
